import { useEffect, useRef, useState } from 'react';
import { client } from './axios';
import { useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { FontBase } from './font-base';
import { labels } from './labels';

export function Start() {
  const [searchTerm, setSearchTerm] = useState('');
  const [attendees, setAttendees] = useState<any>([]);
  const [selectedAttendee, setSelectedAttendee] = useState<any>(null);
  const [activeSession, setActiveSession] = useState<boolean>(false);
  const [language, setLanguage] = useState<'swe' | 'eng'>('swe');
  const [disableRefresh, setDisableRefresh] = useState<boolean>(false);

  const [searchParams] = useSearchParams();

  const activeSessionIntervalRef = useRef<any>(null);
  const attendeeIntervalRef = useRef<any>(null);

  const track = searchParams.get('track');

  const getActiveSession = async () => {
    try {
      const response = await client.get('/gothiacup/session', {
        params: {
          track,
        },
      });

      if (response.data === '') {
        setActiveSession(false);
        return;
      }

      const { language } = response.data;

      setLanguage(language);
      setActiveSession(true);

    } catch (error) {
      setActiveSession(false);
    }
  };

  const getAttendees = async () => {
    try {
      const response = await client.get('/gothiacup/attendees');
      setAttendees(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    // Define the cleanup function outside of the effect
    const cleanup = () => {
      if (activeSessionIntervalRef.current) {
        clearInterval(activeSessionIntervalRef.current);
      }
      if (attendeeIntervalRef.current) {
        clearInterval(attendeeIntervalRef.current);
      }
    };

    // Set up the interval
    getActiveSession();
    getAttendees();

    attendeeIntervalRef.current = setInterval(getAttendees, 60000);
    activeSessionIntervalRef.current = setInterval(getActiveSession, 10000);

    // Cleanup on component unmount or before setting up a new interval
    return cleanup;
  }, [track]); // I

  const handleStart = async () => {
    try {
      await client.post('/gothiacup/start', null, {
        params: {
          track,
          badgeNo: selectedAttendee?.badgeNo,
          languageCode: language,
        },
      });
      setActiveSession(true);
      setSelectedAttendee(null);
      setSearchTerm('');
    } catch (error) {
      console.error(error);
    }
  };

  const handleRefresh = () => {
    setDisableRefresh(true);
    getAttendees();

    const timeout = setTimeout(() => {
      setDisableRefresh(false);
      clearTimeout(timeout);
    }, 5000);
  };

  const filteredAttendees =
    searchTerm?.length > 3
      ? attendees.filter(
          (attendee: any) =>
            attendee.person.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            attendee.person.lastName.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : [];

  if (track !== '1' && track !== '2') {
    return (
      <div>
        <h1>Invalid Track in URL: select '1' or '2'</h1>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat bg-zinc-100">
      <img src="https://jirangopub.s3.eu-north-1.amazonaws.com/Files/9962/SEB_Wordmark_RGB_Black.svg" className="h-12 absolute top-8 left-8" alt="SEB" />
      <FontBase />
      <h1 className="absolute top-10 text-4xl font-bold">
        {labels['track'][language]} {track}
      </h1>
      {/* <button className="absolute top-5 right-5 rounded-full bg-slate-400" onClick={handleStop}>
        <i className="ri-close-line text-3xl text-white"></i>
      </button> */}
      {activeSession ? (
        <div className="bg-white border-slate-100 w-96 flex font-semibold gap-2 flex-col p-6  shadow-sm">{labels['lets_go'][language]}</div>
      ) : (
        <div className="bg-white border-slate-100 w-96 flex gap-2 flex-col p-6  shadow-sm">
          <div className="flex gap-x-2 mb-4 ">
            <button
              className={twMerge(
                'rounded-md flex items-center font-semibold   text-sm  gap-x-2',
                language === 'swe' ? 'bg-slate-100 ' : 'bg-white border-slate-100 border '
              )}
              onClick={() => setLanguage('swe')}
            >
              <img src={`https://flagcdn.com/se.svg`} className="h-3" />
              Svenska
            </button>
            <button
              className={twMerge(
                'rounded-md flex items-center font-semibold text-sm  gap-x-2',
                language === 'eng' ? 'bg-slate-100 ' : 'bg-white text-black border-slate-100 border '
              )}
              onClick={() => setLanguage('eng')}
            >
              <img src={`https://flagcdn.com/gb.svg`} className="h-3" />
              English
            </button>
          </div>
          <label className="font-semibold text-sm ">{labels['search_for_attendee'][language]}</label>
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className=" border border-slate-200 p-3 rounded-md text-sm w-full" />

          <div className={twMerge('overflow-y-auto max-h-64 text-sm  bg-slate-50  border-slate-200 divide-y [&>div]:p-3', searchTerm?.length > 3 && 'border ')}>
            {filteredAttendees.map((attendee: any) => (
              <div
                key={attendee.orderId}
                onClick={() => setSelectedAttendee(selectedAttendee?.badgeNo === attendee.badgeNo ? null : attendee)}
                className="flex justify-between cursor-pointer text-sm"
              >
                {attendee.person.firstName} {attendee.person.lastName}
                <div className="font-bold ">{selectedAttendee?.badgeNo === attendee.badgeNo && <i className="ri-check-line text-green-500"></i>}</div>
              </div>
            ))}
          </div>
          {selectedAttendee ? (
            <button
              disabled={!selectedAttendee}
              className="bg-[#007ac7] mt-1 text-white font-semibold text-sm  rounded-md px-2 py-3 disabled:opacity-50"
              onClick={handleStart}
            >
              {labels['start'][language]}
            </button>
          ) : (
            <button
              disabled={disableRefresh}
              className="border border-slate-200 bg-slate-50 mt-1 font-semibold text-sm rounded-md px-2 py-3 disabled:opacity-50"
              onClick={handleRefresh}
            >
              <i className="ri-refresh-line mr-2"></i>
              {labels['sync_list'][language]}
            </button>
          )}
          <p className="text-sm italic mt-2">{labels['refresh_list_helptext'][language]}</p>
        </div>
      )}
    </div>
  );
}
